import { AxiosResponse } from 'axios';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';

import { FormValue } from 'src/containers/MultipleQuotes/useForm';
import useConvertSizeMutation from 'src/queries/useConvertSizeMutation';
import { getSizesById, isImportedProductOnly } from 'src/utils/helper';

type UseConvertSizeHook = {
  index: number;
  product: string;
  manufacturingSizes;
  setValue: UseFormSetValue<FormValue>;
  getValues: UseFormGetValues<FormValue>;
  isFetching: boolean;
  estimatePriceData;
};

type CheckConvertSizeParam = {
  sizeType?: string;
  isSetValue: boolean;
  data?: string;
};

const useConvertSizeMultiple = (props: UseConvertSizeHook) => {
  const { index, product, manufacturingSizes, setValue, getValues } = props;

  const [conversionSize1, setConversionSize1] = useState<string>();
  const [conversionSize2, setConversionSize2] = useState<string>();
  const [conversionSize3, setConversionSize3] = useState<string>();
  const [conversionSize4, setConversionSize4] = useState<string>();
  const [conversionSize5, setConversionSize5] = useState<string>();
  const [conversionSize6, setConversionSize6] = useState<string>();
  const [originalPriceSize2, setOriginalPriceSize2] = useState<string>();
  const conversionSize1MultipleRef = useRef<string[]>([]);

  const handleSuccessConvertSize = (
    res: AxiosResponse<Response.ConvertSize, any>,
    arg: Payload.ConvertSize,
  ) => {
    const { sizeType, index: indexConvertSize } = arg;
    const splitProductThicknessSize = getValues(
      `priceQuotes.${index}.product_thickness`,
    )?.split('x');
    if (
      sizeType === 'product_thickness' &&
      _.size(splitProductThicknessSize) > 1
    ) {
      //Replace the original size with the converted size corresponding to the index
      conversionSize1MultipleRef.current =
        conversionSize1MultipleRef.current.map((value, i) =>
          i === indexConvertSize ? res?.data?.item : value,
        );
    } else {
      setOriginalPriceSize2(res?.data?.item);
      checkConditionConvertionSize({
        sizeType: arg?.sizeType,
        isSetValue: true,
        data: res?.data?.item,
      });
    }
  };

  const { convertSizeMutation } = useConvertSizeMutation({
    handleSuccess: handleSuccessConvertSize,
  });
  const checkConditionConvertionSize = (param: CheckConvertSizeParam) => {
    const { sizeType, isSetValue, data } = param;
    let value;
    switch (sizeType) {
      case 'product_thickness':
        if (isSetValue) {
          setConversionSize1(data);
          setValue(`priceQuotes.${index}.converted_product_thickness`, data);
        } else {
          setValue(`priceQuotes.${index}.converted_product_thickness`, '');
        }
        value = manufacturingSizes?.unitSizes[0];
        break;
      case 'product_width':
        if (isSetValue) {
          setConversionSize2(data);
          setValue(`priceQuotes.${index}.converted_product_width`, data);
        } else {
          setValue(`priceQuotes.${index}.converted_product_width`, '');
        }
        value = manufacturingSizes?.unitSizes[1];
        break;
      case 'product_length':
        if (isSetValue) {
          setConversionSize3(data);
          setValue(`priceQuotes.${index}.converted_product_length`, data);
        } else {
          setValue(`priceQuotes.${index}.converted_product_length`, '');
        }
        value = manufacturingSizes?.unitSizes[2];
        break;
      case 'dimension_4':
        if (isSetValue) {
          setConversionSize4(data);
          setValue(`priceQuotes.${index}.converted_dimension_4`, data || '');
        } else {
          setValue(`priceQuotes.${index}.converted_dimension_4`, '');
        }
        value = manufacturingSizes?.unitSizes[3];
        break;
      case 'dimension_5':
        if (isSetValue) {
          setConversionSize5(data);
          setValue(`priceQuotes.${index}.converted_dimension_5`, data || '');
        } else {
          setValue(`priceQuotes.${index}.converted_dimension_5`, '');
        }
        value = manufacturingSizes?.unitSizes[4];
        break;
      case 'dimension_6':
        if (isSetValue) {
          setConversionSize6(data);
          setValue(`priceQuotes.${index}.converted_dimension_6`, data || '');
        } else {
          setValue(`priceQuotes.${index}.converted_dimension_6`, '');
        }
        value = manufacturingSizes?.unitSizes[5];
        break;
      default:
        value = undefined;
        break;
    }
    return value !== undefined ? value : '';
  };

  const handleConvertSize = (
    inputType:
      | 'product_thickness'
      | 'product_width'
      | 'product_length'
      | 'dimension_4'
      | 'dimension_5'
      | 'dimension_6',
  ) => {
    if (
      isImportedProductOnly({ product: product, checkZ0: false }) &&
      manufacturingSizes?.sizeMustConvert === true &&
      manufacturingSizes?.originSize !==
        checkConditionConvertionSize({
          sizeType: inputType,
          isSetValue: false,
          data: undefined,
        })
    ) {
      const inputTypeValue = getValues(`priceQuotes.${index}.${inputType}`);
      let inputValue;

      if (inputType === 'product_thickness') {
        const sizesById = getSizesById(
          manufacturingSizes?.sizes?.find(
            (item) => item.value === inputTypeValue,
          )?.key,
        );
        inputValue = sizesById || inputTypeValue;
      } else {
        inputValue = inputTypeValue;
      }

      if (inputValue) {
        if (
          inputType === 'product_thickness' &&
          _.size(inputTypeValue.split('x')) > 1
        ) {
          const unitArray = manufacturingSizes?.unitSize1?.split('x');
          const size1Array = inputValue?.split('x');
          const checkHasOtherUnit = unitArray?.some(
            (item) => item !== manufacturingSizes?.originSize,
          );
          if (checkHasOtherUnit) {
            conversionSize1MultipleRef.current = size1Array;
            size1Array?.map((value, index) => {
              convertSizeMutation.mutate({
                originUnit: manufacturingSizes?.originSize,
                unitBeforeConvert: unitArray?.[index],
                unitAfterConvert: manufacturingSizes?.originSize,
                convertValue: value,
                index: index,
                sizeType: inputType,
              });
            });
          }
        } else {
          convertSizeMutation.mutate({
            originUnit: manufacturingSizes?.originSize,
            unitBeforeConvert: checkConditionConvertionSize({
              sizeType: inputType,
              isSetValue: false,
              data: undefined,
            }),
            unitAfterConvert: manufacturingSizes?.originSize,
            convertValue: inputValue,
            sizeType: inputType,
          });
        }
      } else {
        checkConditionConvertionSize({
          sizeType: inputType,
          isSetValue: true,
          data: undefined,
        });
        if (inputType === 'product_thickness') {
          conversionSize1MultipleRef.current = [];
        }
      }
    } else {
      // When not converting size, reset the old conversion values.
      inputType === 'product_width' &&
        setValue(`priceQuotes.${index}.converted_product_width`, '');
      inputType === 'product_length' &&
        setValue(`priceQuotes.${index}.converted_product_length`, '');
    }
  };

  useEffect(() => {
    if (_.size(conversionSize1MultipleRef.current) > 0) {
      setValue(
        `priceQuotes.${index}.converted_product_thickness`,
        conversionSize1MultipleRef.current.join('x'),
      );
    }
  }, [conversionSize1MultipleRef.current]);

  return {
    handleConvertSize,
    checkConditionConvertionSize,
    conversionSize1,
    conversionSize2,
    conversionSize3,
    conversionSize4,
    conversionSize5,
    conversionSize6,
    originalPriceSize2,
    setConversionSize1,
    setConversionSize2,
    setConversionSize3,
    setConversionSize4,
    setConversionSize5,
    setConversionSize6,
    conversionSize1MultipleRef,
  };
};

export default useConvertSizeMultiple;
